import { SagaIterator } from 'redux-saga';
import { call, select, takeEvery } from 'redux-saga/effects';

import {
  ADD_CAMERA,
  AddCameraAction,
  SET_CAMERA_EASE_TYPE,
  SetCameraEasingTypeAction
} from 'js/actionCreators/cameraActions';
import {
  PIN_CAMERAS,
  PinCamerasAction,
  TOGGLE_CAMERA_PINS,
  TogglePinsAction,
  UNPIN_CAMERAS,
  UnpinCamerasAction
} from 'js/actionCreators/scribeActions';
import { CAMERA_EASING_EASEOUT, CAMERA_EASING_LINEAR, CAMERA_EASING_NONE } from 'js/config/consts';
import { getActiveElements, getProjectById } from 'js/sagas/selectors';
import { allCamerasPinned } from 'js/shared/allCamerasPinned';

import { track } from '../mixpanelProvider';

const ADD_CAMERA_EVENT_NAME = 'Add Camera To Current View';
const ADD_CAMERA_TO_ELEMENTS_EVENT_NAME = 'Add Camera To Fit Selected Elements';
function* trackAddCamera({ projectId, fitTo, trigger }: AddCameraAction): SagaIterator {
  const project: ReturnType<typeof getProjectById> = yield select(getProjectById, projectId);
  const selectedElements: ReturnType<typeof getActiveElements> = yield select(getActiveElements);

  if (!project) return;

  let actionName = ADD_CAMERA_EVENT_NAME;
  let elementTypesProp;
  if (fitTo === 'elements') {
    actionName = ADD_CAMERA_TO_ELEMENTS_EVENT_NAME;

    const projectElementMap = new Map(project.elements.map(element => [element.id, element]));
    const selectedElementsTypes = new Set();
    for (const elementId of selectedElements) {
      const element = projectElementMap.get(elementId);
      if (element) {
        selectedElementsTypes.add(element.type);
      }
    }

    if (selectedElementsTypes.size > 0) {
      elementTypesProp = Array.from(selectedElementsTypes);
    }
  }

  yield call(track, actionName, {
    'Scribe ID': project.id,
    'Element Count': selectedElements.length,
    'Element Types': elementTypesProp,
    'Event Trigger': trigger
  });
}

function* trackCameraPins({ elementIds, scribeId, eventTrigger, type }: PinCamerasAction | UnpinCamerasAction) {
  const event = type === PIN_CAMERAS ? 'Pin Camera to Canvas' : 'Unpin Camera from Canvas';
  yield call(track, event, {
    'Scribe ID': scribeId,
    'Event Trigger': eventTrigger,
    'No. of Cameras': elementIds.length
  });
}

function* trackAllCameraPins({ scribeId, eventTrigger }: TogglePinsAction): SagaIterator {
  const scribe: ReturnType<typeof getProjectById> = yield select(getProjectById, scribeId);
  if (!scribe) return;
  const activeSceneCamerasPinned = allCamerasPinned(scribe);

  const event = activeSceneCamerasPinned ? 'Unpin All Cameras from Canvas' : 'Pin All Cameras to Canvas';
  yield call(track, event, {
    'Scribe ID': scribeId,
    'Event Trigger': eventTrigger
  });
}

function* trackCameraEaseType({ easeType, scribeId }: SetCameraEasingTypeAction): SagaIterator {
  const event = 'Set Camera Movement Type';
  let easeTypeParam = '';
  if (easeType === CAMERA_EASING_NONE) {
    easeTypeParam = 'None';
  } else if (easeType === CAMERA_EASING_EASEOUT) {
    easeTypeParam = 'Ease Out';
  } else if (easeType === CAMERA_EASING_LINEAR) {
    easeTypeParam = 'Linear';
  }
  yield call(track, event, {
    'Scribe ID': scribeId,
    'Movement Type': easeTypeParam
  });
}

export default function* trackCameraSagas() {
  yield takeEvery(ADD_CAMERA, trackAddCamera);
  yield takeEvery([PIN_CAMERAS, UNPIN_CAMERAS], trackCameraPins);
  yield takeEvery(TOGGLE_CAMERA_PINS, trackAllCameraPins);
  yield takeEvery(SET_CAMERA_EASE_TYPE, trackCameraEaseType);
}
